import type { UIComponent } from '@gik/core/types/UI';
import bemBlock from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import type { HeadingLevel } from '@gik/ui-legacy/Heading';
import { Heading } from '@gik/ui-legacy/Heading';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon/SvgIcon';
import ChevronDownIcon from '@heroicons/react/solid/ChevronDownIcon';
import React from 'react';

const ACCORDION_TRANSITION_DURATION_MS = 300;

/**
 * Check if an element is scrollable (has overflow set to auto or scroll)
 */
function isScrollable(element: HTMLElement): boolean {
  const style = getComputedStyle(element);
  const overflowY = style.overflowY;
  return (overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight;
}

/**
 * Find the closest scrollable container for an element.
 * Checks for known modal/drawer containers first, then any scrollable ancestor.
 */
function findScrollContainer(element: HTMLElement): HTMLElement {
  // Check for modal content wrapper (most common case for this feature)
  const modalContainer = element.closest('.gik-modal__content-wrapper') as HTMLElement;
  if (modalContainer) return modalContainer;

  // Check for drawer content (Drawer uses __content, not __content-wrapper)
  const drawerContainer = element.closest('.gik-drawer__content') as HTMLElement;
  if (drawerContainer) return drawerContainer;

  // Walk up the DOM tree to find any scrollable ancestor
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    if (isScrollable(parent)) {
      return parent;
    }
    parent = parent.parentElement;
  }

  // Fall back to document
  return document.documentElement;
}

/**
 * Scroll an element into view within a container.
 * Handles modal/drawer containers properly by calculating position relative to the container.
 */
function scrollElementIntoView(element: HTMLElement, container: HTMLElement, offset = 0): void {
  if (container === document.documentElement) {
    // For page-level scrolling, use standard behavior
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  // Calculate the element's position relative to the scroll container
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  // Position of element relative to container's current scroll
  const relativeTop = elementRect.top - containerRect.top + container.scrollTop;
  const scrollTarget = relativeTop + offset;

  container.scrollTo({
    top: scrollTarget,
    behavior: 'smooth',
  });
}

export type AccordionItemProps = {
  header?: React.ReactNode;
  isOpen?: boolean;
  onToggle?(ev: React.MouseEvent<HTMLDivElement>): void;
  onClick?(ev: React.MouseEvent<HTMLDivElement>): void;
  headerAsHeading?: boolean;
  headingLevel?: HeadingLevel;
  headerClasses?: string;
  titleClasses?: string;
  toggleIconClasses?: string;
  contentClasses?: string;
  noHeaderPadding?: boolean;
  /**
   * When true, scrolls the accordion item into view after the open animation completes.
   * Useful for mobile where expanded content may be off-screen.
   * Automatically detects and scrolls within modal/drawer containers.
   * @default false
   */
  scrollIntoViewOnOpen?: boolean;
  /**
   * Custom scroll container element. If not provided, will auto-detect
   * modal/drawer containers or fall back to document.
   */
  scrollContainer?: HTMLElement | null;
  /**
   * Offset in pixels for scroll position (negative values scroll higher).
   * @default -20
   */
  scrollOffset?: number;
} & UIComponent;

export const AccordionItem: React.FC<React.PropsWithChildren<AccordionItemProps>> = React.memo(
  ({
    header,
    children,
    className,
    isOpen,
    onToggle = noop,
    onClick = noop,
    headerAsHeading = false,
    headingLevel = 2,
    headerClasses,
    noHeaderPadding = false,
    contentClasses,
    titleClasses,
    toggleIconClasses,
    scrollIntoViewOnOpen = false,
    scrollContainer,
    scrollOffset = -20,
    ...otherProps
  }) => {
    const bem = bemBlock('accordion-item');
    const itemRef = React.useRef<HTMLDivElement>(null);

    // Scroll into view after opening animation completes
    const scrollAfterOpen = React.useCallback(() => {
      if (!scrollIntoViewOnOpen || !itemRef.current) return;

      // Wait for the CSS transition to complete, then use rAF to ensure render is done
      setTimeout(() => {
        requestAnimationFrame(() => {
          if (itemRef.current) {
            const container = scrollContainer ?? findScrollContainer(itemRef.current);
            scrollElementIntoView(itemRef.current, container, scrollOffset);
          }
        });
      }, ACCORDION_TRANSITION_DURATION_MS + 50);
    }, [scrollIntoViewOnOpen, scrollContainer, scrollOffset]);

    const handleToggle = React.useCallback(
      (ev: React.MouseEvent<HTMLDivElement>) => {
        if (!children) {
          onClick(ev);
          return;
        }

        // Check if we're opening (not currently open)
        // isOpen reflects the state BEFORE this toggle
        const isOpening = !isOpen;

        onToggle(ev);

        // Trigger scroll after opening
        if (isOpening) {
          scrollAfterOpen();
        }
      },
      [children, onClick, onToggle, isOpen, scrollAfterOpen]
    );

    return (
      <div ref={itemRef} className={bem(null, [{ open: isOpen }, 'items-center'], className)} {...otherProps}>
        {header && (
          <section
            className={`${bem(
              'header',
              [
                {
                  'no-padding': noHeaderPadding,
                },
              ],
              [headerClasses]
            )}`}
            onClick={handleToggle}
          >
            {headerAsHeading ? (
              <Heading level={headingLevel} noStyling className={`${bem('title')} ${titleClasses || ''}`}>
                {header}
              </Heading>
            ) : (
              <span className={`${titleClasses ? titleClasses : bem('title')}`}>{header}</span>
            )}
            {children && (
              <div className={`${bem('toggle-icon')} ${toggleIconClasses || ''}`}>
                <SvgIcon Icon={ChevronDownIcon} size="sm" />
              </div>
            )}
          </section>
        )}
        <section className={`${bem('main')} h-0 overflow-hidden`}>
          {children && <div className={`${contentClasses || ''}`}>{children}</div>}
        </section>
      </div>
    );
  }
);
